import { createAnimation } from '@ionic/vue';

/**
 * Smooth, liquid tab transition animation for bottom navigation
 *
 * Characteristics:
 * - Short duration so tab changes feel immediate
 * - Gentle easing for smooth motion
 * - Subtle scale effect (99% ↔ 100%) for polish
 * - Soft opacity fade
 *
 * @param {HTMLElement} baseEl - Base element
 * @param {Object} opts - Animation options
 * @returns {Animation} Ionic animation instance
 */
export function snappyTabTransition(baseEl, opts) {
  const enteringEl = opts.enteringEl;
  const leavingEl = opts.leavingEl;

  const tabEasing = 'cubic-bezier(0.2, 0, 0, 1)';

  const enteringAnimation = createAnimation()
    .addElement(enteringEl)
    .duration(180)
    .easing(tabEasing)
    .fromTo('opacity', '0', '1')
    .fromTo('transform', 'scale(0.99)', 'scale(1)');

  const leavingAnimation = createAnimation()
    .addElement(leavingEl)
    .duration(180)
    .easing(tabEasing)
    .fromTo('opacity', '1', '0')
    .fromTo('transform', 'scale(1)', 'scale(0.99)');

  return createAnimation().addAnimation([enteringAnimation, leavingAnimation]);
}

/**
 * Instant (zero-duration) tab transition.
 *
 * Used when a tab is tapped while a previous transition is still playing: the
 * running slide is interrupted and the newly-tapped page is shown immediately,
 * so rapid taps land on the last one instead of queueing behind each animation.
 *
 * @param {HTMLElement} baseEl - Base element
 * @param {Object} opts - Animation options
 * @returns {Animation} Ionic animation instance
 */
export function instantTabTransition(baseEl, opts) {
  const enteringEl = opts.enteringEl;
  const leavingEl = opts.leavingEl;

  const animations = [
    createAnimation().addElement(enteringEl).duration(0).fromTo('opacity', '1', '1'),
  ];

  if (leavingEl) {
    animations.push(
      createAnimation().addElement(leavingEl).duration(0).fromTo('opacity', '0', '0'),
    );
  }

  return createAnimation().addAnimation(animations);
}
